import React, { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Row,
  Col
} from 'reactstrap';
import classnames from 'classnames';
import TableContainer from '../../../../Components/Common/TableContainerReactTable';
import FormInput from '../common/FormInput';
import SelectInput from '../common/SelectInput';

export interface LocationData {
  storeName: string;
  name: string;
  addr1: string;
  addr2: string;
}

interface AssociateLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (location: LocationData) => void;
}

const mockLocations: LocationData[] = [
  { storeName: 'Default Location', name: 'General', addr1: '', addr2: '' },
  { storeName: 'ChinaAU', name: 'China Stock AU', addr1: '', addr2: '' },
  { storeName: 'WMS Test', name: 'WMS Test', addr1: '', addr2: '' },
  { storeName: 'Keilor Park', name: 'Keilor Park', addr1: '20 Wright Road Keilor Park', addr2: 'Adore Beauty' },
  { storeName: 'Offsite Storage', name: 'Offsite Storage', addr1: '', addr2: '' },
  { storeName: 'Ultrapak', name: 'Offsite Ultrapak', addr1: '', addr2: '' },
  { storeName: 'Northside Brands', name: 'Northside Brands', addr1: 'Level 1, 421 High St', addr2: 'NORTHCOTE VIC 3070' },
  { storeName: 'Southland Store', name: 'Southland Store', addr1: '1239 Nepean Hwy', addr2: 'Level 2' },
];

const searchFieldOptions = ['Store Name', 'Name', 'Addr 1', 'Addr 2'];

const AssociateLocationModal: React.FC<AssociateLocationModalProps> = ({ isOpen, onClose, onSelect }) => {
  const [activeTab] = useState('1');
  const [searchField, setSearchField] = useState('Store Name');
  const [searchValue, setSearchValue] = useState('');
  const [locations, setLocations] = useState<LocationData[]>(mockLocations);
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(null);

  const columns = [
    {
      header: 'Store Name',
      accessorKey: 'storeName',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <span
          className={cell.getValue() === 'Default Location' ? 'text-primary' : 'text-link'}
          style={{ cursor: 'pointer', textDecoration: 'underline' }}
          tabIndex={0}
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
        >
          {cell.getValue()}
        </span>
      )
    },
    { 
      header: 'Name', 
      accessorKey: 'name', 
      enableColumnFilter: false,
      cell: (cell: any) => (
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
        >
          {cell.getValue()}
        </span>
      )
    },
    { 
      header: 'Addr 1', 
      accessorKey: 'addr1', 
      enableColumnFilter: false,
      cell: (cell: any) => (
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
        >
          {cell.getValue()}
        </span>
      )
    },
    { 
      header: 'Addr 2', 
      accessorKey: 'addr2', 
      enableColumnFilter: false,
      cell: (cell: any) => (
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
        >
          {cell.getValue()}
        </span>
      )
    }
  ];

  const handleSearch = () => {
    const filtered = mockLocations.filter(location => {
      let fieldValue = '';
      switch (searchField) {
        case 'Store Name':
          fieldValue = location.storeName;
          break;
        case 'Name':
          fieldValue = location.name;
          break;
        case 'Addr 1':
          fieldValue = location.addr1;
          break;
        case 'Addr 2':
          fieldValue = location.addr2;
          break;
        default:
          fieldValue = location.storeName;
      }
      return fieldValue.toLowerCase().includes(searchValue.toLowerCase());
    });
    setLocations(filtered);
  };

  const handleClear = () => {
    setSearchValue('');
    setLocations(mockLocations);
    setSelectedLocation(null);
  };

  const handleRowClick = (location: LocationData) => {
    setSelectedLocation(location);
  };

  const handleRowDoubleClick = (location: LocationData) => {
    if (onSelect) {
      onSelect(location);
    }
    onClose();
  };

  const handleClears = () => {
    setSelectedLocation(null);
  };

  const handleCancel = () => {
    setSelectedLocation(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>Associate Location</ModalHeader>
      <ModalBody>
        <Nav pills className="nav nav-tabs-custom arrow-navtabs nav-success mb-3">
          <NavItem>
            <NavLink 
              className={classnames({ active: activeTab === '1' })} 
              tabIndex={0} 
              aria-label="Search Tab"
              style={{ cursor: 'pointer' }}
            >
              Search
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTab={activeTab}>
          <TabPane tabId="1">
            <Row className="mb-2 justify-content-center align-items-center gap-2">
              <Col md={4}>
                <SelectInput
                  formData={{ searchField }}
                  setFormData={(obj) => setSearchField(obj.searchField)}
                  label="Search"
                  id="searchField"
                  options={searchFieldOptions}
                />
              </Col>
              <Col md={4}>
                <FormInput
                  id="searchValue"
                  label="For value"
                  value={searchValue}
                  onChange={setSearchValue}
                  type="text"
                />
              </Col>
              <Col md={2} className="d-flex gap-2">
                <button 
                  className="btn btn-soft-primary btn-sm" 
                  style={{ minWidth: 80 }} 
                  onClick={handleSearch}
                >
                  Search
                </button>
                <button 
                  className="btn btn-soft-warning btn-sm" 
                  style={{ minWidth: 80 }} 
                  onClick={handleClear}
                >
                  Clear
                </button>
              </Col>
              <Col md={1}></Col>
            </Row>
            <TableContainer
              columns={columns}
              data={locations}
              isGlobalFilter={false}
              customPageSize={10}
              tableClass="table-centered align-middle table-nowrap mb-0"
              theadClass="table-light"
              thClass="px-3 py-2"
            />
          </TabPane>
        </TabContent>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-end">
        <div className="d-flex gap-2 justify-content-end">
          <button 
            className="btn btn-soft-warning btn-sm me-2" 
            style={{ minWidth: 80 }} 
            onClick={handleClears}
          >
            Clears
          </button>
          <button 
            className="btn btn-soft-dark btn-sm" 
            style={{ minWidth: 80 }} 
            onClick={handleCancel}
          >
            Cancel
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default AssociateLocationModal; 